import { useEffect } from "react";

import {
  ExperienceCategory,
  ExperienceModule,
  GetExperiencesListResponseBodyType,
} from "@intelligems/ig-types";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { getNextPageParam } from "../getNextPageParam";
import { experienceKeys, historyKeys } from "../queryKeys";

const getExperiencesList = async (options: {
  category?: ExperienceCategory;
  module?: ExperienceModule;
  page?: number;
  limit?: number;
  includeSchedules?: boolean;
}) => {
  const resp = await api.get<GetExperiencesListResponseBodyType>(
    `/v2/experiences-list`,
    {
      params: {
        page: options.page,
        limit: options.limit,
        category: options.category,
        module: options.module,
        includeSchedules: options.includeSchedules,
      },
    }
  );
  return normalizeExperiencesListPage(resp.data);
};

const getExperiencesListQueryKey = (options: {
  category?: ExperienceCategory;
  module?: ExperienceModule;
}) => {
  if (!options.module && options.category === "experiment") {
    return experienceKeys.experimentList;
  }

  if (!options.module && options.category === "personalization") {
    return experienceKeys.homeOverviewPersonalizationList;
  }

  return experienceKeys.experiencesList(options);
};

export const normalizeExperiencesListPage = (
  page: GetExperiencesListResponseBodyType
): GetExperiencesListResponseBodyType => ({
  ...page,
  experiencesList: page.experiencesList ?? [],
});

export const getExperiencesListNextPageParam = (
  lastPage: GetExperiencesListResponseBodyType,
  limit: number
): number | undefined =>
  getNextPageParam(lastPage.experiencesList, lastPage.page, limit);

export const useGetExperiencesList = <T extends ExperienceCategory>({
  category,
  module,
  fetchAllPages,
  enabled = true,
}: {
  category?: ExperienceCategory;
  module?: ExperienceModule;
  fetchAllPages?: boolean;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();
  const limit = 50;
  const experiences = useInfiniteQuery<
    GetExperiencesListResponseBodyType,
    Error,
    GetExperiencesListResponseBodyType
  >({
    initialPageParam: 1,
    queryKey: getExperiencesListQueryKey({ category, module }),
    enabled,
    queryFn: async ({ pageParam = 1 }: { pageParam: unknown }) =>
      getExperiencesList({
        category,
        module,
        page: pageParam as number,
        limit,
        // Include each experience's schedules in the list payload so list
        // tables (e.g. StatusRenderer) don't fire a per-row schedules request.
        includeSchedules: true,
      }),
    getNextPageParam: (lastPage) =>
      getExperiencesListNextPageParam(lastPage, limit),
    getPreviousPageParam: (firstPage) => {
      if (firstPage.page && firstPage.page > 1) {
        return firstPage.page - 1;
      }
      return undefined;
    },
    select: (data) => {
      return {
        experiencesList: data.pages.flatMap(
          (page) => page.experiencesList ?? []
        ),
      };
    },
  });

  useEffect(() => {
    if (
      fetchAllPages &&
      experiences.hasNextPage &&
      !experiences.isFetchingNextPage
    ) {
      void experiences.fetchNextPage();
    }
  }, [fetchAllPages, experiences.hasNextPage, experiences.isFetchingNextPage]);

  useEffect(() => {
    for (const exp of experiences.data?.experiencesList || []) {
      queryClient.setQueryData(historyKeys.one(exp.id), {
        history: exp.history,
      });
    }
  }, [experiences.data?.experiencesList, queryClient]);

  return {
    ...experiences,
    isFetchingAllPages:
      Boolean(fetchAllPages) &&
      Boolean(experiences.hasNextPage || experiences.isFetchingNextPage),
    isFullyLoaded:
      !fetchAllPages ||
      (!experiences.hasNextPage && !experiences.isFetchingNextPage),
  };
};
